/* eslint-disable max-classes-per-file */
import { ExecContext, LocalState } from '@apollo/client/core/LocalState';
import type { FetchResult } from '@apollo/client/link/core/types';
import {
	isField,
	isInlineFragment,
	mergeDeepArray,
	resultKeyNameFromField,
	shouldInclude,
} from '@apollo/client/utilities';
import type {
	DocumentNode,
	FieldNode,
	FragmentDefinitionNode,
	InlineFragmentNode,
	SelectionNode,
	SelectionSetNode,
} from 'graphql';
import { isEmpty } from 'lodash/fp';
import { invariant } from 'ts-invariant';

export class NetworkError extends Error {
	extensions: any;
	constructor(message: string, extensions) {
		super(message);
		this.extensions = extensions;
	}
}

// @ts-ignore
export class GqlEngine<TCacheShape> extends LocalState<TCacheShape> {
	/**
	 * Apollo LocalState does not support error handling. We handle response errors in `resolveSelectionSet` and here we
	 * unwrap the `data` and the `errors` and append it to the result.
	 */
	public async runResolvers<TData>({
		document,
		remoteResult,
		context,
		variables,
		onlyRunForcedResolvers = false,
	}: {
		document: DocumentNode | null;
		remoteResult: FetchResult<TData>;
		context?: Record<string, any>;
		variables?: Record<string, any>;
		onlyRunForcedResolvers?: boolean;
	}): Promise<FetchResult<TData>> {
		if (document) {
			// @ts-ignore
			return this.resolveDocument(
				document,
				remoteResult.data,
				context,
				variables,
				this.getFragmentMatcher(),
				onlyRunForcedResolvers
			).then(({ result: { data, errors } }) => {
				return { ...remoteResult, data, errors };
			});
		}

		return remoteResult;
	}

	/**
	 * HACK -- override of private method
	 * GraphQL requires fields to exist in the result otherwise throws for missing field, but missing field in REST API
	 * is OK for non-required fields. This returns `null` if selection does not exists in the response, which prevent
	 * the missing field error to ever occur.
	 */
	private async resolveField(field: FieldNode, rootValue: any, execContext: ExecContext) {
		return (
			super
				// @ts-ignore
				.resolveField(field, rootValue, execContext)
				.then((value) => (value === undefined ? null : value))
		);
	}

	/**
	 * HACK -- override of private method
	 * Apollo LocalState does not support error handling, this is complete copy of LocalState source-code with few
	 * additions which allows us to handle response errors. All additions are denoted with comment "CUSTOM"
	 */
	private async resolveSelectionSet<TData>(
		selectionSet: SelectionSetNode,
		rootValue: TData,
		execContext: ExecContext
	): Promise<TData> {
		const rootSelection = isEmpty(rootValue); // CUSTOM - the custom logic is triggered only for rootSelection
		const errors = rootSelection ? ([] as any) : undefined; // CUSTOM - stack for the errors

		const { fragmentMap, context, variables } = execContext;
		const resultsToMerge: TData[] = [rootValue];

		const execute = async (selection: SelectionNode): Promise<void> => {
			if (!shouldInclude(selection, variables)) {
				// Skip this entirely.
				return;
			}

			if (isField(selection)) {
				const fieldName = resultKeyNameFromField(selection);
				// eslint-disable-next-line consistent-return
				return this.resolveField(selection, rootValue, execContext).then(
					(fieldResult) => {
						if (typeof fieldResult !== 'undefined') {
							resultsToMerge.push({
								[fieldName]: fieldResult,
							} as TData);
						}
					},
					errors && // CUSTOM - handle error responses from root fields
						((error) => {
							if (error instanceof NetworkError) {
								throw error;
							}
							errors.push({ fieldName, path: [fieldName], extensions: error });
							// add null to the results, otherwise the query will crash
							resultsToMerge.push({ [fieldName]: null } as unknown as TData);
						})
				);
			}

			let fragment: InlineFragmentNode | FragmentDefinitionNode;

			if (isInlineFragment(selection)) {
				fragment = selection;
			} else {
				// This is a named fragment.
				fragment = fragmentMap[selection.name.value];
				invariant(fragment, `No fragment named ${selection.name.value}`);
			}

			if (fragment && fragment.typeCondition) {
				const typeCondition = fragment.typeCondition.name.value;
				if (execContext.fragmentMatcher(rootValue, typeCondition, context)) {
					// eslint-disable-next-line consistent-return
					return this.resolveSelectionSet(fragment.selectionSet, rootValue, execContext).then(
						(fragmentResult) => {
							resultsToMerge.push(fragmentResult);
						}
					);
				}
			}
		};

		return Promise.all(selectionSet.selections.map(execute)).then(
			rootSelection // CUSTOM - rootSelection returns wrapped `data` and `errors`
				? () =>
						({
							data: mergeDeepArray(resultsToMerge),
							errors,
						} as any)
				: () => mergeDeepArray(resultsToMerge)
		);
	}
}
